import { useEffect, useState } from "react"
import { Modal } from "../../components/modal/Modal"
import { CheckCircleIcon } from "@heroicons/react/16/solid"
import { SelectField } from "../../shared/ui/SelectField"
import { MultiSelect } from "../../shared/ui/MultiSelect"
import { SectionsEditor, CourseSection } from "../../features/courses/ui/SectionsEditor"
import {
  AdminCourseItem,
  AdminCourseUpdatePayload,
  CourseDetail,
  getAdminCourseApi,
  updateCourseApi,
} from "../../features/courses/api/course.api"
import { listMentorsApi, UserProfileItem } from "../../features/users/api/profile.api"
import { CategoryItem, listCategoriesApi } from "../../features/categories/api/category.api"
import { listCoursePriceTiersApi, PriceTierOption } from "../../features/payments/api/payments.api"

type Props = {
  open: boolean
  onClose: () => void
  course: CourseDetail
  /** Called after a successful save so the parent can refetch the (differently-shaped) public course detail. */
  onSaved: () => void
}

const inputCls =
  "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{children}</label>
)

/** Switch toggle with strong, distinct on/off colors (not the pale blue/gray-300 default). */
const Toggle = ({
  checked,
  onChange,
  label,
  hint,
  onColor = "bg-emerald-500",
  offColor = "bg-gray-400",
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
  onColor?: string
  offColor?: string
}) => (
  <label className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-3">
    <span className="text-sm text-gray-700">
      {label}
      {hint && <span className="block text-xs text-gray-400 mt-0.5">{hint}</span>}
    </span>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 shadow-inner ${checked ? onColor : offColor}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all ${checked ? "left-5" : "left-0.5"}`} />
    </button>
  </label>
)

const LEVEL_OPTIONS = [
  { value: "beginner", label: "Начальный" },
  { value: "intermediate", label: "Средний" },
  { value: "advanced", label: "Продвинутый" },
]

const PRICE_TYPES = [
  { value: "free", label: "Бесплатно" },
  { value: "paid", label: "Платно" },
  { value: "mixed", label: "Смешанный" },
] as const

const TABS = [
  { key: "details", label: "Детали" },
  { key: "relations", label: "Цена и связи" },
  { key: "settings", label: "Настройки" },
] as const
type TabKey = (typeof TABS)[number]["key"]

type FormState = {
  title: string
  description: string
  level: "beginner" | "intermediate" | "advanced"
  durationMinutes: number
  language: string
  priceType: "free" | "paid" | "mixed"
  priceAmount: number
  categoryIds: string[]
  mentorIds: string[]
  sections: CourseSection[]
  isPromoted: boolean
  isActive: boolean
}

const formFromAdmin = (a: AdminCourseItem): FormState => ({
  title: a.title,
  description: a.description ?? "",
  level: a.level,
  durationMinutes: a.duration_minutes ?? 0,
  language: a.language,
  priceType: a.price.type,
  priceAmount: a.price.amount,
  categoryIds: a.category_ids ?? [],
  mentorIds: a.mentor_ids ?? [],
  sections: a.sections ?? [],
  isPromoted: a.is_promoted ?? false,
  isActive: a.is_active ?? true,
})

const buildPayload = (original: AdminCourseItem, form: FormState): AdminCourseUpdatePayload => {
  const payload: AdminCourseUpdatePayload = {}
  if (form.title !== original.title) payload.title = form.title
  if (form.description !== (original.description ?? "")) payload.description = form.description
  if (form.level !== original.level) payload.level = form.level
  if (form.durationMinutes !== (original.duration_minutes ?? 0)) payload.duration_minutes = form.durationMinutes
  if (form.language !== original.language) payload.language = form.language
  if (form.priceType !== original.price.type || form.priceAmount !== original.price.amount) {
    payload.price = { type: form.priceType, amount: form.priceType === "free" ? 0 : form.priceAmount }
  }
  if (JSON.stringify(form.categoryIds) !== JSON.stringify(original.category_ids ?? [])) payload.category_ids = form.categoryIds
  if (JSON.stringify(form.mentorIds) !== JSON.stringify(original.mentor_ids ?? [])) payload.mentor_ids = form.mentorIds
  if (JSON.stringify(form.sections) !== JSON.stringify(original.sections ?? [])) payload.sections = form.sections
  if (form.isPromoted !== (original.is_promoted ?? false)) payload.is_promoted = form.isPromoted
  if (form.isActive !== (original.is_active ?? true)) payload.is_active = form.isActive
  return payload
}

export const EditCourseModal = ({
  open,
  onClose,
  course,
  onSaved,
}: Props) => {
  const [tab, setTab] = useState<TabKey>("details")
  const [admin, setAdmin] = useState<AdminCourseItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState<FormState | null>(null)

  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [mentors, setMentors] = useState<UserProfileItem[]>([])
  const [priceTiers, setPriceTiers] = useState<PriceTierOption[]>([])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open) return
    setTab("details")
    setAdmin(null)
    setForm(null)
    setError("")
    setLoading(true)

    Promise.all([getAdminCourseApi(course.id), listCategoriesApi(), listMentorsApi(), listCoursePriceTiersApi()])
      .then(([adminRes, categoriesRes, mentorsRes, tiersRes]) => {
        setAdmin(adminRes.data)
        setForm(formFromAdmin(adminRes.data))
        setCategories(categoriesRes.data.items)
        setMentors(mentorsRes.data.items)
        setPriceTiers(tiersRes.data.items)
      })
      .catch(() => setError("Не удалось загрузить данные курса"))
      .finally(() => setLoading(false))
  }, [open, course.id])

  const handleSave = async () => {
    if (!admin || !form || !form.title.trim()) return
    setSaving(true)
    setError("")
    try {
      const payload = buildPayload(admin, form)
      if (Object.keys(payload).length > 0) {
        await updateCourseApi(course.id, payload)
      }
      onSaved()
      onClose()
    } catch {
      setError("Не удалось сохранить изменения")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Редактировать курс">
      <div className="space-y-5 pb-4">

        {/* Tabs */}
        <div className="flex items-center gap-1 -mx-6 -mt-5 mb-1 px-6 border-b border-gray-100">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-gray-400">Загрузка…</div>
        )}

        {!loading && form && (
          <>
            {tab === "details" && (
              <div className="space-y-5">
                <div>
                  <FieldLabel>Название</FieldLabel>
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Название курса"
                    className={inputCls}
                  />
                </div>

                <div>
                  <FieldLabel>Описание</FieldLabel>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Уровень</FieldLabel>
                    <SelectField
                      value={form.level}
                      onChange={(v) => setForm({ ...form, level: v as FormState["level"] })}
                      options={LEVEL_OPTIONS}
                      placeholder="Выберите уровень"
                    />
                  </div>
                  <div>
                    <FieldLabel>Язык</FieldLabel>
                    <input
                      value={form.language}
                      onChange={(e) => setForm({ ...form, language: e.target.value })}
                      placeholder="ru"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>Длительность (минут)</FieldLabel>
                  <input
                    value={form.durationMinutes === 0 ? "" : String(form.durationMinutes)}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) setForm({ ...form, durationMinutes: e.target.value === "" ? 0 : Number(e.target.value) })
                    }}
                    placeholder="например, 120"
                    className={inputCls}
                  />
                </div>

                <div>
                  <FieldLabel>Разделы курса</FieldLabel>
                  <SectionsEditor sections={form.sections} setSections={(sections) => setForm({ ...form, sections })} />
                </div>
              </div>
            )}

            {tab === "relations" && (
              <div className="space-y-5">
                <div>
                  <FieldLabel>Тип цены</FieldLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {PRICE_TYPES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setForm({ ...form, priceType: t.value })}
                        className={`py-2.5 rounded-xl border text-sm font-medium transition-all ${
                          form.priceType === t.value
                            ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {form.priceType !== "free" && (
                  <div>
                    <FieldLabel>Цена (уровень IAP, $)</FieldLabel>
                    <SelectField
                      value={form.priceAmount ? String(form.priceAmount) : ""}
                      onChange={(v) => setForm({ ...form, priceAmount: Number(v) })}
                      placeholder="Выберите уровень цены"
                      options={priceTiers.map((t) => ({
                        value: String(t.tier),
                        label: `$${t.tier} (${(t.price_usd_cents / 100).toFixed(2)})`,
                      }))}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Цена — фиксированный уровень из сетки Apple/Google IAP, свободный ввод отключён.
                    </p>
                  </div>
                )}

                <div>
                  <FieldLabel>Менторы</FieldLabel>
                  <MultiSelect
                    values={form.mentorIds}
                    onChange={(mentorIds) => setForm({ ...form, mentorIds })}
                    options={mentors.map((m) => ({ value: m.user_id, label: m.full_name ?? m.user_id }))}
                    placeholder="Выберите менторов…"
                  />
                </div>

                <div>
                  <FieldLabel>Категории</FieldLabel>
                  <MultiSelect
                    values={form.categoryIds}
                    onChange={(categoryIds) => setForm({ ...form, categoryIds })}
                    options={categories.map((c) => ({ value: c.id, label: c.title }))}
                    placeholder="Выберите категории…"
                  />
                </div>
              </div>
            )}

            {tab === "settings" && (
              <div className="space-y-2.5">
                <Toggle
                  checked={form.isPromoted}
                  onChange={(v) => setForm({ ...form, isPromoted: v })}
                  label="Продвигается"
                  hint="Курс будет выделен как рекомендуемый"
                  onColor="bg-amber-500"
                />
                <Toggle
                  checked={form.isActive}
                  onChange={(v) => setForm({ ...form, isActive: v })}
                  label="Активен"
                  hint="Неактивный курс скрыт для пользователей (404 по прямой ссылке)"
                  onColor="bg-emerald-500"
                />
              </div>
            )}
          </>
        )}

        {error && (
          <div className="px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-1 bg-white border-t border-gray-100 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Отмена
          </button>

          <button
            onClick={handleSave}
            disabled={!form || !form.title.trim() || saving || loading || (form.priceType !== "free" && !form.priceAmount)}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
          >
            {saving
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <CheckCircleIcon className="w-4 h-4" />
            }
            Сохранить
          </button>
        </div>

      </div>
    </Modal>
  )
}
